/*
 * File Created: Friday, 2nd June 2023 8:18:16 am
 * Author: Chenzier Kabrabam
 * Copyright (c) - 2018-2023 Addble Solutions Private Limited. All Rights Reserved.
 */

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { Theme, useTheme } from "@mui/material";
import { IoRemove, IoAdd } from "react-icons/io5";
import { MdOutlineAddShoppingCart } from "react-icons/md";
import React from "react";
import { CartContext } from "../../context/cartContext.context";
import SocialMediaIcon from "../SocialMediaIcon/SocialMediaIcon";
import Button from "../Button";
import type { ViewProductDescription as  ViewProductDescriptionProps } from "../../types/viewProductDescription/viewProductDescription.type";
import { Colors } from "../../constants/color";

function ViewProductDescription(props: ViewProductDescriptionProps): JSX.Element {
    const theme: Theme = useTheme();
    const [quantity, setQuantity] = React.useState("1");
    const { addToCart } = React.useContext(CartContext);
  
    const socialMedia: Array<JSX.Element> = props.share.map((data): JSX.Element => {
      return (
        <SocialMediaIcon
          key={data.name}
          name={data.name}
          src={data.src}
          sx={{
            width: "30px",
            height: "30px",
            border: `1px solid ${Colors.silver}`,
            borderRadius: "50%",
            color: Colors.boulder,
            fontSize: "16px",
            transition: "border 900ms ease, color 900ms ease",
            "&:hover": {
              color: Colors.fallow,
              border: `1px solid ${Colors.fallow}`,
            }
          }}
        />
      )
    })
  
    function handleQtyIncrement() {
      setQuantity((prev) => (parseInt(prev, 10) + 1).toString())
    }
    function handleQtyDecrement() {
      setQuantity((prev) => parseInt(prev, 10) !== 1 ? (parseInt(prev, 10) - 1).toString() : "1")
    }
  
    return (
      <Box
        sx={{
          width: { xs: "100%", md: "50%" },
          display: "flex",
          flexDirection: "column",
          gap: 2,
          // backgroundColor: "green",
          paddingInline: { xs: 0, md: theme.spacing(2)},
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.6,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: theme.typography.pxToRem(22), md: theme.typography.pxToRem(24)},
            }}
          >
            {props.name}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 1.5,
            }}
          >
            <Typography
              variant="h5"
              sx={{
                display: props.discount !== 0 ? "block" : "none",
                color: Colors.apricot,
                fontSize: theme.typography.pxToRem(24),
              }}
            >
              ₹{props.discount}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                textDecoration: props.discount !== 0 ? "line-through" : "none",
                color: Colors.silver,
                fontSize: theme.typography.pxToRem(24),
              }}
            >
              ₹{props.mrp}
            </Typography>
          </Box>
          <Typography
            variant="body1"
            sx={{
              color: Colors.boulder,
            }}
          >{props.description}</Typography>
          <Box
            sx={{
              display: "flex", 
              flexDirection: "row",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Typography variant="body1" sx={{ color: Colors.dove_gray }}>Qty:</Typography>
            <Box
                sx={{
                  width: "fit-content",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  border: `1px solid ${Colors.dove_gray}`,
                  paddingBlock: theme.spacing(0.5),
                  gap: 3,
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleQtyDecrement}
                  disabled={quantity === "1"}
                >
                  <IoRemove />
                </IconButton>
                <Typography variant="h6" sx={{ color: Colors.dove_gray }}>{quantity}</Typography>
                <IconButton
                  size="small"
                  onClick={handleQtyIncrement}
                >
                  <IoAdd />
                </IconButton>
              </Box>
          </Box>
          <Box>
            <Button
              sx={{
                borderRadius: 0,
                padding: "10px 30px",
                border: `1px solid ${Colors.fallow}`,
                color: Colors.fallow,
                "&.MuiButtonBase-root:hover": {
                  backgroundColor: Colors.fallow,
                  color: Colors.white,
                  border: `1px solid ${Colors.fallow}`,
                },
              }}
              startIcon={<MdOutlineAddShoppingCart />}
              title="ADD TO CART"
              onClick={() => addToCart(props.id, quantity)}
            />
          </Box>
        </Box>
        <Divider orientation="horizontal" />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: 2,
            }}
          >
            <Typography variant="body1" sx={{ color: Colors.boulder }}>Category: </Typography>
            {props.category.map((data) => {
              return <Typography className="category-name" key={data.id} sx={{ color: Colors.dove_gray }}>{data.name}</Typography>
            })}
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: Colors.boulder
              }}
            >
              Share:
            </Typography>
            {socialMedia}
          </Box>
        </Box>
      </Box>
    );
  }

export default ViewProductDescription;